import { JSX, useState } from "react";
import { TableCell } from "@mui/material";
import AddShoppingCartOutlined from "@mui/icons-material/AddShoppingCartOutlined";

// COMPONENTS
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import BackOrderBadge from "@/main/components/shared/backOrderBadge/backOrderBadge";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import FavoriteButton from "@/main/components/shared/favoriteButton/favoriteButton";
import NotQuotedOrAwaitingAnswerInfo from "@/main/components/shared/card/notQuotedOrAwaitingAnswerInfo";
import PriceDiffPercent from "@/main/components/shared/priceDiffPercent/priceDiffPercent";
import SkeletonRowSupplierAssigned from "./skeletonRowSupplierAssigned";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import StarRatingPartially from "@/main/components/shared/starRating/starRatingPartially";
import * as StylesShared from "@/main/components/shared/styles";

// SERVICES
import { QuoteSelectSuppliersDataResponse, QuoteSelectSuppliersDataResponseItems } from "@/main/models/response";
import { getTranslateByKey, isGuidEmpty, isNullUndefined, isShowControlByElementId } from "@/main/services/utils";
import { userIsSupplier } from "@/main/services/utilsUser";
import { IMAGE_DEFAULT } from "@/main/services/constants";
import { StatusQuoteSupplierItem } from "@/main/services/enum/StatusQuoteSupplierItem";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";
import { Controls } from "@/main/services/constants/constantsControl";
import { CartState } from "@/main/store/cart";

// COMPONENTS/STYLES LOCAL
import ModalPartDetails from "@/main/screens/order/createorder/steps/stepTwo/modalPartDetails/modalPartDetails";
import * as S from "./styles";
import { formatAddCart } from "../utilsBoxCart";
import { AddCartType } from "../types";
import { QuoteSuppItemStatusEnum } from "@/main/services/enum/QuoteSuppItemStatusEnum";

interface IRowSupplierAssignedProps {
	dataCart: CartState;
	addCart: AddCartType;
	dataParts: QuoteSelectSuppliersDataResponseItems | null;
	isLoading: boolean;
	guidPart: string;
	guidQuote: string;
	externalPartId: string;
	integration: boolean;
	isQuoteOpen: boolean;
	isQuoteManual: boolean;
	isWaitingRelease?: boolean;
	onReplaceSupplier?: (item: QuoteSelectSuppliersDataResponse) => void;
}
function RowSupplierAssigned({
	dataParts,
	isLoading,
	guidPart,
	externalPartId,
	guidQuote,
	integration,
	isQuoteManual,
	isQuoteOpen,
	isWaitingRelease = false,
	addCart,
	dataCart,
	onReplaceSupplier,
}: IRowSupplierAssignedProps) {
	const [showUnnassignedSuppliers, setShowUnnassignedSuppliers] = useState(false);
	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const isUserSupplier = userIsSupplier();
	const isAwaitingReleaseSupplier = isUserSupplier && isWaitingRelease;
	const showButtonAddCart =
		(isQuoteManual ? isShowControlByElementId(Controls.IdAddPartToCart) : true) && !isAwaitingReleaseSupplier;

	const rowsHeader = [
		<StylesShared.RowHeaderTextCenter key="image">
			{getTranslateByKey("quoteCard.image")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="supplier">
			{getTranslateByKey("companyType.supplier")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="sku">{getTranslateByKey("quoteCard.sku")}</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="partTypes">
			{getTranslateByKey("information.partTypes")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="product">
			{getTranslateByKey("quoteCard.product")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="deliveryDays">
			{getTranslateByKey("information.deliveryDays")}
		</StylesShared.RowHeaderTextCenter>,
		<StylesShared.RowHeaderTextCenter key="rating">
			{getTranslateByKey("quoteCard.rating")}
		</StylesShared.RowHeaderTextCenter>,
		showPrices ? (
			<StylesShared.RowHeaderTextCenter key="price">
				{getTranslateByKey("information.price")}
			</StylesShared.RowHeaderTextCenter>
		) : (
			false
		),
		showButtonAddCart ? (
			<StylesShared.RowHeaderTextCenter key="addToCart">
				{getTranslateByKey("quoteCard.addToCart")}
			</StylesShared.RowHeaderTextCenter>
		) : (
			false
		),
	].filter(Boolean);

	const sizeColumns = [
		"100px", // Image
		"responsive", // Supplier
		"9%", // SKU
		"9%", // Part Type
		"14%", // Product
		"10%", // Delivery Days
		"150px", // Rating
		showPrices ? "130px" : false, // Price
		showButtonAddCart ? "90px" : false, // Cart
	].filter(Boolean) as string[];

	//#region Handle function
	function handleClickCart(partItem: QuoteSelectSuppliersDataResponse) {
		const { formattedPart, formattedSupplierCart } = formatAddCart({
			externalPartId,
			guidPart,
			guidQuote,
			partItem,
		});

		if (!onReplaceSupplier) {
			addCart(formattedSupplierCart, formattedPart.guidQuoteSupplierItem);
			return;
		}
		onReplaceSupplier(partItem);
	}
	//#endregion

	//#region Validations
	const isUnavailablePart = (part: QuoteSelectSuppliersDataResponse) =>
		(part.shipingIntegrationIsUnavailable && part.shipingIntegrationIsUnavailable) ||
		part.idQuoteSuppItemStatus === StatusQuoteSupplierItem.Pending ||
		part.notQuoted ||
		!part.price ||
		isNullUndefined(part.deliveryDays);
	//#endregion

	//#region Render functions
	const renderPartType = (part: QuoteSelectSuppliersDataResponse) => {
		if (isNullUndefined(part.partType) || isNullUndefined(part.idPartType)) return "";
		if (part.idPartType !== PartTypeEnum.OEM) {
			return `${part.partType} ${part.partBrandDescription ? `(${part.partBrandDescription})` : ""}`;
		}

		return part.partType;
	};
	const renderDeliveryDays = (part: QuoteSelectSuppliersDataResponse) =>
		`${part.deliveryDays} ${getTranslateByKey("information.deliveryDays")}`;
	//#endregion

	if (!isLoading && isNullUndefined(dataParts)) return <ResultNotFoundComponent />;

	const NUMBER_COLUMNS_FIXED = 2;
	const NUMBER_COLUMNS_DINAMIC = rowsHeader.length - NUMBER_COLUMNS_FIXED;

	const isLoadingCart = (idPart: string) =>
		(dataCart.cartItems.isLoading || dataCart.cartItems.isLoadingUpdate) &&
		dataCart.cartItems.idPartIsLoading === idPart;
	const disabledCart =
		dataCart.cartItems.isLoading || dataCart.cartItems.isLoadingUpdate || (integration && isQuoteOpen);

	//#region Mini Components
	const AreaPartTableRow = ({ item }: { item: QuoteSelectSuppliersDataResponse }): JSX.Element => {
		const [openModalDetails, setOpenModalDetails] = useState(false);

		function handleClickImage() {
			setOpenModalDetails(true);
		}
		function closeModalDetails() {
			setOpenModalDetails(false);
		}

		return (
			<>
				<S.PartTableRow $isRecommended={item.isRecomendedSupplier} $isBlocked={isUnavailablePart(item)}>
					<TableCell>
						<S.CustomColumn>
							<S.ImagePart
								$showCursor={!isNullUndefined(item.image)}
								src={item.image ? item.image : IMAGE_DEFAULT}
								alt=""
								onClick={handleClickImage}
							/>
						</S.CustomColumn>
					</TableCell>
					<TableCell>
						<S.SupplierColumn title={item.supplier}>
							<span>{item.supplier}</span>
							<FavoriteButton isFavorite={item.isFavoriteSupplier} notClickable={true} onToggleFavorite={() => {}} />
						</S.SupplierColumn>
					</TableCell>
					{isUnavailablePart(item) ? (
						<TableCell colSpan={NUMBER_COLUMNS_DINAMIC}>
							<S.AwaitingAnswer>
								{item.shipingIntegrationIsUnavailable ? (
									getTranslateByKey("information.unavailableErrorConnectionToShippingCompany")
								) : (
									<NotQuotedOrAwaitingAnswerInfo
										reasonDescription={item.reasonDescription}
										reason={item.reason}
										notQuoted={item.notQuoted}
										isQuoteManual={isQuoteManual}
										isQuoteOpen={isQuoteOpen}
									/>
								)}
							</S.AwaitingAnswer>
						</TableCell>
					) : (
						<>
							<TableCell>
								<S.CustomColumn title={item.partCode}>{item.partCode}</S.CustomColumn>
							</TableCell>
							<TableCell>
								<S.CustomColumn title={renderPartType(item)}>{renderPartType(item)}</S.CustomColumn>
							</TableCell>
							<TableCell>
								<S.CustomColumn title={item.partTitle}>
									<b>{item.partTitle}</b>
								</S.CustomColumn>
							</TableCell>
							<TableCell>
								<S.DeliveryDaysColumn title={renderDeliveryDays(item)}>
									<span>{renderDeliveryDays(item)}</span>
									{item.backOrder && <BackOrderBadge />}
								</S.DeliveryDaysColumn>
							</TableCell>
							<TableCell>
								<StarRatingPartially rating={!isNullUndefined(item.score) ? item.score : 0} />
							</TableCell>
							{showPrices && (
								<TableCell>
									<S.PriceColumn title={item.priceFormatted}>
										{item.priceFormatted}
										{!isUserSupplier && !isNullUndefined(item.priceDiffPercent) && (
											<PriceDiffPercent diffPercent={Number(item.priceDiffPercent)} />
										)}
										{!isAwaitingReleaseSupplier && !isNullUndefined(item.systemReferencePrice) && (
											<p style={{ margin: "0" }} translate="no">
												{getTranslateByKey("information.systemReferencePrice")}: {item.systemReferencePriceFormatted}
											</p>
										)}
									</S.PriceColumn>
								</TableCell>
							)}
							{showButtonAddCart && (
								<TableCell>
									<S.CartColumn>
										<S.CartIconButton
											id={`id-createOrder-button-cart-${item.partTitle}`}
											disabled={disabledCart || item.idQuoteSuppItemStatus == QuoteSuppItemStatusEnum.Cart}
											onClick={() => handleClickCart(item)}
										>
											{isLoadingCart(item.guidQuoteSupplierItem) ? (
												<StylesShared.SpinnerLoading />
											) : (
												<AddShoppingCartOutlined fontSize="medium" />
											)}
										</S.CartIconButton>
									</S.CartColumn>
								</TableCell>
							)}
						</>
					)}
				</S.PartTableRow>
				<KogModalSideComponent open={openModalDetails} closeModal={() => closeModalDetails()}>
					{openModalDetails && (
						<ModalPartDetails
							closeModal={() => closeModalDetails()}
							guid={!isGuidEmpty(item.supplierInventoryGuid) ? item.supplierInventoryGuid : item.guidQuoteSupplierItem}
							quoteSupplierItem={isGuidEmpty(item.supplierInventoryGuid)}
						/>
					)}
				</KogModalSideComponent>
			</>
		);
	};
	//#endregion

	const assignedSupplierItems = dataParts?.selectSupplierItems.filter((item) => !isUnavailablePart(item)) ?? [];
	const unnassignedSupplierItems = dataParts?.selectSupplierItems.filter((item) => isUnavailablePart(item)) ?? [];

	return (
		<S.Container>
			<BoxGrid
				rowHeaders={rowsHeader}
				sizeColumns={sizeColumns}
				marginCustom="0"
				propsTable={{
					style: {
						minWidth: "950px",
					},
				}}
			>
				{isLoading ? (
					Array.from(new Array(4)).map((_, index) => (
						<SkeletonRowSupplierAssigned key={index} numberColumns={rowsHeader.length} />
					))
				) : isNullUndefined(dataParts) ? (
					<ResultNotFoundComponent />
				) : (
					<>
						{assignedSupplierItems.map((item) => (
							<AreaPartTableRow key={item.guidQuoteSupplierItem} item={item} />
						))}

						{showUnnassignedSuppliers &&
							unnassignedSupplierItems.map((item) => <AreaPartTableRow key={item.guidQuoteSupplierItem} item={item} />)}
						<S.PartTableRow>
							<TableCell colSpan={rowsHeader.length} style={{ textAlign: "center" }}>
								<S.TextButtonOpen type="button" onClick={() => setShowUnnassignedSuppliers((prev) => !prev)}>
									{showUnnassignedSuppliers
										? getTranslateByKey("information.close")
										: getTranslateByKey("information.viewSuppliersNotCapableToQuote")}
								</S.TextButtonOpen>
							</TableCell>
						</S.PartTableRow>
					</>
				)}
			</BoxGrid>
		</S.Container>
	);
}

export default RowSupplierAssigned;
